import { Divider, Drawer, Stack } from '@mui/material'
import { ContentChip, DisplayUserLink, InfoBox, LoadingSkeleton, tokens, User, type ExternalParticipantDisplayData } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { IconButton, TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import { ArrowUpRightIcon, BellIcon, EyeIcon, GearIcon, QuestionIcon, XIcon } from '@phosphor-icons/react'
import { TextButton } from 'ComponentLibrary/src/piazza/buttons/TextButton/TextButton'
import { isEmpty } from 'lodash'
import { ParticipantsAutocomplete } from 'ComponentLibrary/src/widgets/Participants/ParticipantsAutocomplete'
import { Plus } from 'react-feather'
import { useState } from 'react'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { ID, UUID } from 'ComponentLibrary/src/common/uuid'
import { useNavigate } from 'react-router-dom'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type ObserversDrawerProps = {
    users: User[]
    currentUser?: User
    isEditObservers: boolean
    isOrderPending?: boolean
    setEditObservers: (value: boolean) => void
    persistObservers?: (newUUIds: UUID[]) => void
    isPersistingObservers?: boolean
    emptyObserversMessage?: string
    canEditOtherRoles?: boolean
    disabled?: boolean
    showNavigateToSettingsForObservers?: boolean
    observerUUIDs: UUID[]
    externalParticipantUsers?: ExternalParticipantDisplayData[]
    addObserver?: (uuid: UUID) => void
    removeObserver: (index: number) => void
    orderId?: UUID
    orderAssignedAppraiserId?: string
    orderAssignedCustomerUsers: ID[]
    navigateToNotificationSettings?: () => void
    withoutAppraisers?: boolean
    copy?: Partial<ObserversDrawerCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

export type ObserversDrawerCopy = {
    title: string
    addObserversLabel: string
    observeOrder: string
    observerCount: (count: number) => string
    editObservers: string
    responsible: string
    responsibleRemoveTooltip: string
    infoTitle: string
    infoText: string
    notificationSettings: string
}

const DEFAULT_OBSERVERS_DRAWER_COPY: ObserversDrawerCopy = {
    title: 'Beobachter bearbeiten',
    addObserversLabel: 'Weitere Beobachter hinzufügen...',
    observeOrder: 'Auftrag beobachten',
    observerCount: (count) => `${count} Beobachter des Auftrags`,
    editObservers: 'Beobachter bearbeiten',
    responsible: 'Zuständig',
    responsibleRemoveTooltip: 'Auftragszuständige können nicht als Beobachter/in entfernt werden.',
    infoTitle: 'Was sind Beobachter?',
    infoText:
        'Beobachter werden per Mail über alle Neuigkeiten zu einem Auftrag informiert, die sie in ihren Benachrichtigungseinstellungen aktiviert haben. Dazu zählen u.A.: Änderungen des Auftragsstatus, verfügbare Entwürfe, neue Nachrichten und Aufgabenupdates.',
    notificationSettings: 'Zu den Benachrichtigungseinstellungen',
}

export const ObserversDrawer = ({
    orderId,
    orderAssignedCustomerUsers,
    isOrderPending,
    users,
    currentUser,
    isEditObservers,
    setEditObservers,
    persistObservers,
    isPersistingObservers,
    emptyObserversMessage,
    disabled,
    showNavigateToSettingsForObservers,
    observerUUIDs,
    externalParticipantUsers = [],
    addObserver,
    removeObserver,
    navigateToNotificationSettings,
    orderAssignedAppraiserId,
    withoutAppraisers,
    copy,
    userLinkCopy,
}: ObserversDrawerProps) => {
    const navigate = useNavigate()
    const resolvedCopy = { ...DEFAULT_OBSERVERS_DRAWER_COPY, ...copy }

    const [newObservers, setNewObservers] = useState<User[]>([])

    const observerUsers = users.filter((user) => observerUUIDs.includes(user.uuid) && (!withoutAppraisers || user.role !== 'appraiser'))
    const hasShownObservers = observerUsers.length > 0 || externalParticipantUsers.length > 0

    const addNewObservers = (newObserverUUIDs: UUID[]) => persistObservers?.([...observerUUIDs, ...newObserverUUIDs])

    const handleAddObserversButtonClick = () => {
        addNewObservers(newObservers.map(({ uuid }) => uuid))
        setNewObservers([])
    }

    return (
        <Drawer open={isEditObservers} onClose={() => setEditObservers(false)} anchor="right" sx={{ zIndex: 1550 }}>
            <Stack direction="column" width="580px">
                <Stack direction="row" padding={tokens.SizingSpacing300} justifyContent="space-between" alignItems="center">
                    <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                        <Typography variant="headline1" color={tokens.ColorTextBoldest}>
                            {resolvedCopy.title}
                        </Typography>
                        <LoadingIndicator isPending={!!isPersistingObservers} />
                    </Stack>
                    <IconButton
                        variant="tertiary"
                        size="medium"
                        icon={<XIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />}
                        onClick={() => setEditObservers(false)}
                    />
                </Stack>
                <Divider />
                <Stack direction="column" padding={tokens.SizingSpacing300} gap={tokens.SizingSpacing400}>
                    <>
                        <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                            <ParticipantsAutocomplete
                                users={users.filter((user) => !user.is_platform_user && !observerUUIDs.includes(user.uuid))}
                                newParticipants={newObservers}
                                setNewParticipants={setNewObservers}
                                disabled={disabled}
                                label={resolvedCopy.addObserversLabel}
                                userLinkCopy={userLinkCopy}
                            />
                            <IconButton
                                size="medium"
                                icon={<Plus />}
                                onClick={handleAddObserversButtonClick}
                                disabled={isEmpty(newObservers)}
                            />
                        </Stack>
                        {currentUser && !disabled && !observerUUIDs.includes(currentUser.uuid) && (
                            <Stack direction="row" gap={tokens.SizingSpacing100} justifyContent="space-between">
                                <DisplayUserLink size="medium" user={currentUser} disablePortal copy={userLinkCopy} />
                                <TextButton
                                    icon={<BellIcon />}
                                    label={resolvedCopy.observeOrder}
                                    disabled={currentUser.is_platform_user}
                                    onClick={() => addObserver?.(currentUser.uuid)}
                                />
                            </Stack>
                        )}
                        <Stack direction="column" gap={tokens.SizingSpacing150}>
                            <Typography variant="textM" color={tokens.ColorTextDefault}>
                                {resolvedCopy.observerCount(observerUsers.length + externalParticipantUsers.length)}
                            </Typography>
                            <Stack direction="row" justifyContent={'space-between'} alignItems={'center'}>
                                {!hasShownObservers && (
                                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                                        {emptyObserversMessage}
                                    </Typography>
                                )}
                                {showNavigateToSettingsForObservers &&
                                    (isOrderPending || !orderId ? (
                                        <LoadingSkeleton />
                                    ) : (
                                        <TinyButton
                                            leftIcon={<EyeIcon />}
                                            label={resolvedCopy.editObservers}
                                            rightIcon={<ArrowUpRightIcon />}
                                            onClick={() => navigate(`/orders/${orderId}/overview?edit=true`)}
                                        />
                                    ))}
                            </Stack>
                            {observerUsers.map((observer) => {
                                const responsibleForOrder =
                                    orderAssignedCustomerUsers?.includes(observer.id) || observer.uuid === orderAssignedAppraiserId
                                return (
                                    <Stack
                                        direction="row"
                                        key={observer.uuid}
                                        gap={tokens.SizingSpacing150}
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <DisplayUserLink user={observer} size="medium" disablePortal copy={userLinkCopy} />
                                        <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                            {currentUser?.id && responsibleForOrder && (
                                                <ContentChip size="small" color="royal" label={resolvedCopy.responsible} />
                                            )}
                                            {!disabled &&
                                                (responsibleForOrder ? (
                                                    <IconButton
                                                        tooltip={resolvedCopy.responsibleRemoveTooltip}
                                                        size="small"
                                                        variant="tertiary"
                                                        icon={<XIcon color={tokens.ColorIconDefault} weight="bold" />}
                                                        onClick={() => undefined}
                                                        disabled
                                                    />
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        variant="tertiary"
                                                        icon={<XIcon color={tokens.ColorIconDefault} weight="bold" />}
                                                        onClick={() =>
                                                            removeObserver(observerUUIDs.findIndex((uuid) => uuid === observer.uuid))
                                                        }
                                                        disabled={isPersistingObservers}
                                                    />
                                                ))}
                                        </Stack>
                                    </Stack>
                                )
                            })}
                            {externalParticipantUsers.map((observer) => {
                                const responsibleForOrder = observer.uuid === orderAssignedAppraiserId
                                return (
                                    <Stack
                                        direction="row"
                                        key={observer.uuid}
                                        gap={tokens.SizingSpacing150}
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <DisplayUserLink user={observer} size="medium" disablePortal isExternal copy={userLinkCopy} />
                                        <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                            {currentUser?.id && responsibleForOrder && (
                                                <ContentChip size="small" color="royal" label={resolvedCopy.responsible} />
                                            )}
                                        </Stack>
                                    </Stack>
                                )
                            })}
                        </Stack>
                    </>
                    <InfoBox variant="ordinary" icon={<QuestionIcon />}>
                        <Stack direction="column" gap={tokens.SizingSpacing100}>
                            <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                                {resolvedCopy.infoTitle}
                            </Typography>
                            <Typography variant="textS" color={tokens.ColorTextBold}>
                                {resolvedCopy.infoText}
                            </Typography>
                            {navigateToNotificationSettings && (
                                <TinyButton
                                    leftIcon={<GearIcon />}
                                    label={resolvedCopy.notificationSettings}
                                    rightIcon={<ArrowUpRightIcon />}
                                    onClick={navigateToNotificationSettings}
                                />
                            )}
                        </Stack>
                    </InfoBox>
                </Stack>
            </Stack>
        </Drawer>
    )
}
